import { redirect, type LoaderFunctionArgs } from 'react-router';

import { exchangeSsoCode } from '@features/shared/authentication/exchange-sso-code';

import {
  accessTokenService,
  refreshTokenService,
  sessionStorageService,
} from '@shared/lib/storage';

type SsoCallbackLoaderParams = {
  loginPath: string;
  errorPath: string;
};

// Loader, not a page effect: runs exactly once per navigation, so re-renders and StrictMode
// cannot re-process the callback after `ssoPending` is cleared (see router CLAUDE.md).
// The redirect replaces the history entry, so `code` never stays in the URL.
export const ssoCallbackLoader =
  (params: SsoCallbackLoaderParams) =>
  async ({ request }: LoaderFunctionArgs) => {
    const { loginPath, errorPath } = params;
    const searchParams = new URL(request.url).searchParams;

    if (searchParams.get('error')) throw redirect(errorPath);

    const pending = sessionStorageService('ssoPending').get();
    const codeVerifier = sessionStorageService('ssoCodeVerifier').get();
    const code = searchParams.get('code');

    if (!pending) throw redirect(loginPath);

    sessionStorageService('ssoPending').remove();
    sessionStorageService('ssoCodeVerifier').remove();

    if (!code || !codeVerifier) throw redirect(errorPath);

    try {
      const response = await exchangeSsoCode({ body: { code, codeVerifier } });
      accessTokenService.set(response.accessToken);
      refreshTokenService.set(response.refreshToken);
    } catch {
      throw redirect(errorPath);
    }

    throw redirect('/');
  };
